import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import routes_web from 'connections/routes_web';
import { useIdentityFiles } from './moduleServices/useIdentityFiles';
import { useInstanceState } from './moduleServices/useInstanceState';
import { searchIdentityFiles } from './moduleServices/identity_services';

import NavbarTop from './moduleComponents/NavbarTop';
import NavbarBottom from './moduleComponents/NavbarBottom';
import DrawerFile from './moduleComponents/DrawerFile';
import CreateFileModal from './moduleComponents/CreateFileModal';
import UploadModal from './moduleComponents/UploadModal';
import MainOverview from './tab_overview/main_overview';
import MainExplorer from './tab_explorer/main_explorer';
import MainSettings from './tab_settings/main_settings';

const SEARCH_DEBOUNCE_MS = 300;

const IdentityMain = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createFolderPrefix, setCreateFolderPrefix] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFolderPrefix, setUploadFolderPrefix] = useState('');
  const searchReqId = useRef(0);

  const { files, summary, isLoading, addOrUpdateFile, removeFile, refresh } = useIdentityFiles();
  const { state: instanceState, isLoading: instanceStateLoading, refresh: refreshInstanceState } = useInstanceState();

  const handleFileClick = useCallback((file) => {
    setSelectedFile(file);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const statePath = instanceState?.state_path;

  const handleFileUpdate = useCallback((updatedFile) => {
    addOrUpdateFile(updatedFile);
    // STATE.md may have just been edited through the drawer — keep the
    // dashboard in sync without requiring a manual tab switch.
    if (statePath && updatedFile?.path === statePath) {
      refreshInstanceState();
    }
  }, [addOrUpdateFile, refreshInstanceState, statePath]);

  const handleFileDelete = useCallback((path) => {
    removeFile(path);
    setDrawerOpen(false);
    refresh();
    if (statePath && path === statePath) {
      refreshInstanceState();
    }
  }, [removeFile, refresh, refreshInstanceState, statePath]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'explorer') {
      setSearchQuery('');
      setSearchResults([]);
    }
    if (tab === 'overview') {
      refreshInstanceState();
    }
  };

  const openCreate = useCallback((folderPrefix = '') => {
    setCreateFolderPrefix(folderPrefix || '');
    setCreateOpen(true);
    setActiveTab('explorer');
  }, []);

  const openUpload = useCallback((folderPrefix = '') => {
    setUploadFolderPrefix(folderPrefix || '');
    setUploadOpen(true);
    setActiveTab('explorer');
  }, []);

  const handleCreated = useCallback((result) => {
    addOrUpdateFile(result);
    refresh();
    setSelectedFile(result);
    setDrawerOpen(true);
  }, [addOrUpdateFile, refresh]);

  const handleUploaded = useCallback(() => {
    refresh();
  }, [refresh]);

  // Debounced API search
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    setSearchLoading(true);
    const reqId = ++searchReqId.current;
    const timer = setTimeout(async () => {
      try {
        const data = await searchIdentityFiles(q);
        if (searchReqId.current === reqId) {
          setSearchResults(Array.isArray(data) ? data : []);
        }
      } catch {
        if (searchReqId.current === reqId) setSearchResults([]);
      } finally {
        if (searchReqId.current === reqId) setSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 p-2 pb-20">
      <div className="max-w-5xl w-full mx-auto pt-14">

        {activeTab === 'overview' && (
          <MainOverview
            instanceState={instanceState}
            instanceStateLoading={instanceStateLoading}
            files={files}
            isLoading={isLoading}
            onFileClick={handleFileClick}
            onRefresh={refreshInstanceState}
          />
        )}
        {activeTab === 'explorer' && (
          <MainExplorer
            files={files}
            isLoading={isLoading}
            onFileClick={handleFileClick}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            searchResults={searchResults}
            searchLoading={searchLoading}
            currentPath={explorerPath}
            onPathChange={setExplorerPath}
            onCreateClick={openCreate}
            onUploadClick={openUpload}
          />
        )}
        {activeTab === 'settings' && (
          <MainSettings
            summary={summary}
            onSettingsSaved={refreshInstanceState}
          />
        )}
      </div>

      <DrawerFile
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        file={selectedFile}
        onUpdate={handleFileUpdate}
        onDelete={handleFileDelete}
      />

      <CreateFileModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        folderPrefix={createFolderPrefix}
        onCreated={handleCreated}
      />

      <UploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        folderPrefix={uploadFolderPrefix}
        onUploaded={handleUploaded}
      />

      <NavbarTop
        onHomeClick={() => navigate(routes_web.accounts_home)}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <NavbarBottom
        activeTab={activeTab}
        handleTabChange={handleTabChange}
      />
    </div>
  );
};

export default IdentityMain;
